import { useEffect, useState } from "react";
import {
  Server,
  Users,
  CheckCircle,
  Monitor,
  Clock,
  RefreshCw,
} from "lucide-react";

const API =
  "https://cadence-api.lightspeedphotonics.com/api/cadence-license";

export default function CadenceLicense() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState("");

  const fetchData = async () => {
    try {
      const res = await fetch(API);
      const json = await res.json();

      setData(json);
      setLastRefresh(new Date().toLocaleTimeString());
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();

    const interval = setInterval(fetchData, 10000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <RefreshCw className="animate-spin text-red-600" size={45} />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Header */}

      <div className="bg-white rounded-2xl shadow-sm border p-6">

        <div className="flex justify-between items-center flex-wrap">

          <div>

            <h1 className="text-3xl font-bold text-gray-800">
              Cadence License Monitor
            </h1>

            <p className="text-gray-500 mt-2">
              Live License Status
            </p>

          </div>

          <div className="text-right">

            <div
              className={`inline-flex items-center px-4 py-2 rounded-full font-semibold ${
                data.serverStatus === "ONLINE"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              <Server size={18} className="mr-2" />
              {data.serverStatus}
            </div>

            <p className="text-sm text-gray-400 mt-2">
              Updated {lastRefresh}
            </p>

          </div>

        </div>

      </div>

      {/* Summary */}

      <div className="grid md:grid-cols-3 gap-6">

        <SummaryCard
          title="Total Licenses"
          value={data.summary.totalLicenses}
          icon={<Users size={28} />}
          color="blue"
        />

        <SummaryCard
          title="In Use"
          value={data.summary.licensesInUse}
          icon={<Monitor size={28} />}
          color="red"
        />

        <SummaryCard
          title="Available"
          value={data.summary.licensesAvailable}
          icon={<CheckCircle size={28} />}
          color="green"
        />

      </div>

      {/* Active Users */}

      <div className="bg-white rounded-2xl shadow-sm border p-6">

        <h2 className="text-2xl font-bold mb-5">
          Active Users
        </h2>

        {data.activeUsers.length === 0 ? (

          <div className="text-green-600 font-semibold">
            ✅ No licenses are currently in use.
          </div>

        ) : (

          <div className="overflow-x-auto">

            <table className="min-w-full">

              <thead>

                <tr className="border-b">

                  <th className="text-left py-3">User</th>

                  <th className="text-left py-3">Computer</th>

                  <th className="text-left py-3">Started</th>

                </tr>

              </thead>

              <tbody>

                {data.activeUsers.map((user, index) => (

                  <tr
                    key={index}
                    className="border-b hover:bg-gray-50"
                  >

                    <td className="py-4 font-medium">
                      {user.username}
                    </td>

                    <td className="py-4">

                      <div className="flex items-center gap-2">

                        <Monitor size={16} />

                        {user.machine}

                      </div>

                    </td>

                    <td className="py-4">

                      <div className="flex items-center gap-2">

                        <Clock size={16} />

                        {user.start}

                      </div>

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        )}

      </div>

    </div>
  );
}

function SummaryCard({ title, value, icon, color }) {

  const colors = {
    red: "bg-red-50 text-red-700",
    green: "bg-green-50 text-green-700",
    blue: "bg-blue-50 text-blue-700",
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-6">

      <div className="flex justify-between items-center">

        <div>

          <p className="text-gray-500">
            {title}
          </p>

          <h2 className="text-4xl font-bold mt-3">
            {value}
          </h2>

        </div>

        <div
          className={`w-16 h-16 rounded-xl flex items-center justify-center ${colors[color]}`}
        >
          {icon}
        </div>

      </div>

    </div>
  );
}